import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mergeRefs } from 'react-merge-refs';
import classNames from 'classnames';
import { kebabCase } from 'lodash';
import Tooltip from 'react-tooltip';
import { CaretDownIcon, CaretUpIcon } from '@seismic/icons';

import Overlay, { useOverlayConfig } from '../../Overlay';
import ToolbarButton from '../ToolbarButton';

import Color from './Color';
import Colors from './colors';

export { Colors };

const ToolbarColorPicker = React.forwardRef(
  (
    {
      currentColor = Colors.Red,
      datatest,
      disabled,
      onColorSelect = () => {},
      overlayConfig,
      tooltip,
      tooltipClassName,
      ...props
    },
    ref
  ) => {
    const [showColorsPanel, setShowColorsPanel] = useState(false);
    const panelRef = useRef();
    const buttonRef = useRef();

    const { t } = useTranslation();

    const toggleColorsPanel = useCallback(() => {
      if (!disabled) {
        setShowColorsPanel(!showColorsPanel);
      }
      props?.action?.();
    }, [disabled, showColorsPanel]);

    const closeColorsPanel = useCallback(
      e => {
        if (e.target !== panelRef.current) {
          setShowColorsPanel(false);
        }
      },
      [panelRef.current]
    );

    const handleColorSelect = useCallback(
      color => {
        onColorSelect(color);
        setShowColorsPanel(false);
      },
      [onColorSelect]
    );

    const classes = classNames({
      'up-toolbar-color-picker': true,
      disabled
    });

    const stateTooltip = showColorsPanel
      ? t('Toolbar.Button.Tooltip.CloseColorsPanel')
      : t('Toolbar.Button.Tooltip.OpenColorsPanel');

    const tooltipClasses = classNames({
      'up-button-tooltip': true,
      [tooltipClassName]: !!tooltipClassName
    });

    const _overlayConfig = useOverlayConfig(
      {
        closeOnBackdropClick: true,
        onClose: closeColorsPanel,
        origin: buttonRef,
        positions: {
          originX: 'center',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetX: -23,
          offsetY: -27
        },
        ...overlayConfig
      },
      [overlayConfig]
    );

    return (
      <div className={classes} datatest={datatest}>
        <ToolbarButton
          {...props}
          icon={<Color highlight theme="light" value={currentColor} />}
          action={toggleColorsPanel}
          tooltip={tooltip}
          tooltipClassName={tooltipClassName}
          datatest="toolbarColorPikcerButton"
          ref={mergeRefs([ref, buttonRef])}
          disabled={disabled}
        />
        <div
          className="up-colors-panel-state"
          data-tip={stateTooltip}
          data-for={stateTooltip}
          onClick={toggleColorsPanel}
        >
          {showColorsPanel ? <CaretDownIcon /> : <CaretUpIcon />}
          <Tooltip className={tooltipClasses} effect="solid" id={stateTooltip} globalEventOff="click" />
        </div>
        {showColorsPanel && (
          <Overlay {..._overlayConfig}>
            <div className="up-colors-panel" ref={panelRef}>
              {Object.entries(Colors).map(([name, value]) => (
                <Color
                  contrasty={name === 'White'}
                  highlight={value === currentColor}
                  key={name}
                  name={kebabCase(name)}
                  onClick={handleColorSelect}
                  value={value}
                />
              ))}
            </div>
          </Overlay>
        )}
      </div>
    );
  }
);

export default ToolbarColorPicker;
